import * as React from 'react'
import {DrawSource} from '../../core/enums/DrawSource'
import {DrawType} from '../../core/enums/DrawType'
import {IPopupDispatcher} from '../../core/interfaces/dispatchers'
import {Feature, LineString, Point, VectorLayer, getLength} from '../../core/models'
import {getMapService} from '../../core/reducers/map'
import {MapService} from '../../core/services'
import appConfig from '../../reducers/appConfig'

interface Props {
  left: string
  popupDispatcher: IPopupDispatcher
  isRadiusButtonActive: boolean
  handleIsRadiusButtonStateOnClick: () => void
}

interface OlClickEventParams {
  coordinate: [number, number]
  pixel: [number, number]
  originalEvent: MouseEvent
  type: string
}

interface MeasureEditProps {
  feature: Feature
  popupDispatcher: IPopupDispatcher
}

const MeasureEdit: React.FC<MeasureEditProps> = ({feature, popupDispatcher}) => {
  const initial = (() => {
    const v = feature.get('Radius')
    return v ? parseFloat(v).toFixed(2) : getLength(feature.getGeometry()).toFixed(2)
  })()
  const [value, setValue] = React.useState<string>(initial)

  return (
    <div>
      <input
        className="form-control"
        value={value}
        type="number"
        onChange={e => {
          const val = parseFloat(e.target.value).toFixed(2)
          feature.set('Radius', val)
          setValue(val)
        }}
      />
      <br />
      <button
        type="button"
        className="button width"
        onClick={() => {
          const layer = getMapService()
            .getLayers()
            .find(l => l.moreInfo.urls[0] == appConfig.reg.measures.url) as VectorLayer
          layer.getSource().removeFeature(feature as any)
          popupDispatcher.removePopups(['measureEdit'])
        }}
      >
        Изтрий
      </button>
      <br />
      <button
        type="button"
        className="button width"
        onClick={() => {
          popupDispatcher.removePopups(['measureEdit'])
        }}
      >
        ОК
      </button>
    </div>
  )
}

const getRadius = (a: Point, b: Point, c: Point): {radius: number; center: Point} => {
  const d1 = new Point([b.y() - a.y(), a.x() - b.x()])
  const d2 = new Point([c.y() - a.y(), a.x() - c.x()])
  const k: number = d2.x() * d1.y() - d2.y() * d1.x()
  if (!(k < -0.00001 || k > 0.00001)) {
    return {center: new Point([]), radius: Infinity}
  }
  const s1 = new Point([(a.x() + b.x()) / 2, (a.y() + b.y()) / 2])
  const s2 = new Point([(a.x() + c.x()) / 2, (a.y() + c.y()) / 2])
  const l: number = d1.x() * (s2.y() - s1.y()) - d1.y() * (s2.x() - s1.x())
  const m: number = l / k
  const center = new Point([s2.x() + m * d2.x(), s2.y() + m * d2.y()])
  const radius = getLength(
    new LineString([
      [a.x(), a.y()],
      [center.x(), center.y()]
    ])
  )
  return {center, radius}
}

export const RadiusTool: React.FC<Props> = ({
  left,
  popupDispatcher,
  isRadiusButtonActive,
  handleIsRadiusButtonStateOnClick
}) => {
  const pointerTraceRef = React.useRef(false)
  const latestFeatRef = React.useRef<Feature | undefined>(undefined)

  const getMap = () => (getMapService() as MapService).map

  const mapOnClick = React.useCallback(
    (event: any) => {
      const e = event as OlClickEventParams
      const result: Feature[] = []
      const map = getMap()
      const pixel = e.pixel
      map.forEachFeatureAtPixel(pixel, (f, layer) => {
        if ((layer as any).moreInfo.urls[0] == appConfig.reg.measures.url) {
          result.push(f as any)
        }
      })
      if (result.length > 0 && result[0].get('Radiusable')) {
        popupDispatcher.addPopups({
          measureEdit: {
            content: <MeasureEdit popupDispatcher={popupDispatcher} feature={result[0]} />,
            header: <div>Редакция на раиус</div>,
            width: '400px'
          }
        })
      }
    },
    [popupDispatcher]
  )

  const setPointerTrace = React.useCallback(() => {
    if (!pointerTraceRef.current) {
      pointerTraceRef.current = true
      getMap().on('click', mapOnClick)
    }
  }, [mapOnClick])

  const activateDraw = React.useCallback(() => {
    setPointerTrace()
    getMapService().drawing.activateDraw(
      DrawType.LineString,
      DrawSource.Default,
      () => {
        /* noop */
      },
      f => {
        const coord = (f.getGeometry() as LineString).getCoordinates()
        const radius = getRadius(new Point(coord[0]), new Point(coord[1]), new Point(coord[2]))
        const line1 = new LineString([coord[1], coord[0]])
        const line2 = new LineString([coord[1], coord[2]])
        const minLen = line1.getLength() > line2.getLength() ? line2 : line1
        let maxLen = line1.getLength() < line2.getLength() ? line2 : line1
        const endc = maxLen.getCoordinateAt(minLen.getLength() / maxLen.getLength())
        maxLen = new LineString([coord[1], endc])
        const line = new LineString([
          minLen.getCoordinates()[1],
          minLen.getCoordinates()[0],
          maxLen.getCoordinates()[1]
        ])
        const layer = getMapService()
          .getLayers()
          .find(l => l.moreInfo.urls.indexOf(appConfig.reg.measures.url) != -1)! as VectorLayer
        const feat = new Feature()
        feat.set('Radiusable', true)
        feat.set('Radius', radius.radius.toFixed(1))
        feat.setGeometry(line)
        layer.getSource().addFeature(feat)
        latestFeatRef.current = feat
      },
      3
    )
  }, [setPointerTrace])

  React.useEffect(() => {
    const mapService = getMapService()
    if (isRadiusButtonActive && mapService?.drawing) {
      activateDraw()
    }
  }, [isRadiusButtonActive, activateDraw])

  return (
    <div className="measure-tool" style={{left}} title="Радиус" onClick={handleIsRadiusButtonStateOnClick}>
      <img
        alt=""
        src="resources/icons/radius.png"
        style={{width: '28px', height: '28px', backgroundColor: isRadiusButtonActive ? '#26751b' : '#5c83b3b5'}}
      />
    </div>
  )
}
